import { gql } from "@apollo/client";
import { alpha, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import glLogo from "assets/gl-logo.png";
import Spacer from "atoms/Spacer";
import Stack from "atoms/Stack";
import apolloClient from "helpers/apolloClient";
import { MOBILE_LAYOUT_BREAKPOINT } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_WHITE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { ONSEN_AUTH_TOKEN_KEY } from "helpers/constants";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import rollbar from "helpers/rollbar";
import { SPACING, SPACING_DENSE, SPACING_HALF } from "helpers/spacings";
import useData from "helpers/useData";
import usePlaylistsEnabled from "helpers/usePlaylistsEnabled";
import { progressBar } from "helpers/useProgressBar";
import {
  AccountCircleOutline,
  AccountCog,
  AccountMultiple,
  Close,
  HelpCircleOutline,
  Logout,
  Menu as MenuIcon,
  PlaylistPlay,
  Podium,
  SwapHorizontal,
  TelevisionPlay,
  TrophyVariantOutline,
} from "mdi-material-ui";
import React, { cloneElement, createContext, forwardRef, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useWindowSize } from "react-use-size";

const navbarContext = createContext();
const navMenuContext = createContext();

export default function Navbar() {
  const [data] = useData(gql`
    query NavbarCurrentUser {
      currentUser {
        id
        onboarded
        isPublicLeaguePersona
        potwEnabled
        canAccessReadmin
        email
        fullName
        gamesSeasons(limit: 1) {
          id
        }
      }
      readminUrl
    }
  `);

  const windowSize = useWindowSize();
  const navbarWidth = windowSize.width;
  const location = useLocation();
  const currentPathname = location?.pathname || "";
  const playlistsEnabled = usePlaylistsEnabled();
  const currentUserId = data?.currentUser?.id;
  const currentUserEmail = data?.currentUser?.email;
  const leaderboardsFirstSeasonId = data?.currentUser?.gamesSeasons[0]?.id;

  useEffect(() => {
    rollbar.configure({
      payload: {
        person: {
          id: currentUserId,
          email: currentUserEmail,
        },
      },
    });
  }, [currentUserId, currentUserEmail]);
  const mobileLayout = (navbarWidth || 1) <= MOBILE_LAYOUT_BREAKPOINT;

  // Don't render the navbar until user is logged in
  if (!data?.currentUser) return null;

  return (
    <navbarContext.Provider value={{ navbarWidth }}>
      <Stack
        noGap
        ref={windowSize.ref}
        horizontal
        noWrap
        style={{
          background: COLOR_BLACK,
          boxShadow: `0 1px 4px ${alpha(COLOR_BLACK, 0.2)}`,
          color: COLOR_WHITE_LIGHTER,
          margin: "0 auto",
          width: "100%",
          position: "relative",
          zIndex: 3,
        }}
      >
        <HomeLogo />
        {!mobileLayout && (
          <>
            <NavbarItem label="Games" icon={<TelevisionPlay />} href="/games" currentPathname={currentPathname} />
            {data?.currentUser?.potwEnabled && !data.currentUser.isPublicLeaguePersona && (
              <NavbarItem
                label="Plays of the Week"
                icon={<TrophyVariantOutline />}
                href="/potw"
                currentPathname={currentPathname}
              />
            )}
            {playlistsEnabled && (
              <NavbarItem
                label="Playlists"
                icon={<PlaylistPlay />}
                href="/playlists"
                currentPathname={currentPathname}
              />
            )}
            {leaderboardsFirstSeasonId && (
              <NavbarItem
                label="Leaderboards"
                icon={<Podium />}
                href={`/leaderboards?seasonId=${leaderboardsFirstSeasonId}`}
                currentPathname={currentPathname}
              />
            )}
            <Spacer />

            {!data?.currentUser?.isPublicLeaguePersona && (
              <NavbarMenu icon={<AccountCircleOutline />} label>
                <div
                  style={{
                    backgroundColor: COLOR_WHITE,
                    color: COLOR_BLACK,
                    fontSize: "0.9em",
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontWeight: 500,
                    padding: `${SPACING} 0 0 ${SPACING}`,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    maxWidth: 300,
                    whiteSpace: "nowrap",
                  }}
                >
                  {data?.currentUser.fullName}
                </div>
                <div
                  style={{
                    backgroundColor: COLOR_WHITE,
                    color: COLOR_BLACK,
                    fontSize: "0.8em",
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    padding: `${SPACING} 0 ${SPACING} ${SPACING}`,
                  }}
                >
                  {currentUserEmail}
                </div>
                <div style={{ borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.2)}` }} />
                {data.currentUser.canAccessReadmin && (
                  <NavbarMenuItem icon={<SwapHorizontal />} label="Switch to Admin Console" href={data.readminUrl} />
                )}
                <NavbarMenuItem label="Profile Settings" icon={<AccountMultiple />} href="/people" />
                <NavbarMenuItem
                  label={data?.currentUser?.onboarded ? "Account Settings" : "Set up your Account"}
                  icon={<AccountCog />}
                  href="/account"
                />
                <NavbarMenuItem
                  label="Help"
                  icon={<HelpCircleOutline />}
                  href="https://help.gloryleague.basketball/"
                  target="_blank"
                />
                <div style={{ borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.2)}` }} />
                <NavbarMenuItem
                  label="Logout"
                  icon={<Logout />}
                  onClick={() =>
                    progressBar(async () => {
                      window.localStorage.removeItem(ONSEN_AUTH_TOKEN_KEY);
                      await apolloClient.resetStore();
                    })
                  }
                />
              </NavbarMenu>
            )}
          </>
        )}
        {mobileLayout && (
          <>
            <Spacer />
            <NavbarMenu icon={<MenuIcon />} label>
              <NavbarMenuItem label="Games" icon={<TelevisionPlay />} href="/games" currentPathname={currentPathname} />
              {data?.currentUser?.potwEnabled && !data.currentUser.isPublicLeaguePersona && (
                <NavbarMenuItem
                  label="Plays of the Week"
                  icon={<TrophyVariantOutline />}
                  href="/potw"
                  currentPathname={currentPathname}
                />
              )}
              {playlistsEnabled && (
                <NavbarMenuItem
                  label="Playlists"
                  icon={<PlaylistPlay />}
                  href="/playlists"
                  currentPathname={currentPathname}
                />
              )}
              {leaderboardsFirstSeasonId && (
                <NavbarMenuItem
                  label="Leaderboards"
                  icon={<Podium />}
                  href={`/leaderboards?seasonId=${leaderboardsFirstSeasonId}`}
                  currentPathname={currentPathname}
                />
              )}
              {data.currentUser.canAccessReadmin && (
                <NavbarMenuItem icon={<SwapHorizontal />} label="Switch to Admin Console" href={data.readminUrl} />
              )}
              {!data?.currentUser?.isPublicLeaguePersona && (
                <>
                  <Stack
                    horizontal
                    alignItemsCenter
                    style={{
                      background: alpha(COLOR_BLACK, 0.95),
                      padding: SPACING,
                      color: COLOR_WHITE,
                      fontSize: "0.9em",
                      fontFamily: FONT_FAMILY_GRAPHIK,
                      borderBottom: `1px solid ${alpha(COLOR_WHITE_LIGHTER, 0.1)}`,
                    }}
                  >
                    <AccountCircleOutline
                      style={{
                        fontSize: "1.3em",
                      }}
                    />
                    <Stack alignItemsStart>
                      <div>{data?.currentUser.fullName}</div>
                      <div>{currentUserEmail}</div>
                    </Stack>
                  </Stack>
                  <NavbarMenuItem label="My Profiles" icon={<AccountMultiple />} href="/people" />
                  <NavbarMenuItem
                    label={data?.currentUser?.onboarded ? "My Account & Settings" : "Set up your Account"}
                    icon={<AccountCog />}
                    href="/account"
                  />
                  <NavbarMenuItem
                    label="Help"
                    icon={<HelpCircleOutline />}
                    href="https://help.gloryleague.basketball/"
                    target="_blank"
                  />
                  <NavbarMenuItem
                    label="Logout"
                    icon={<Logout />}
                    onClick={() =>
                      progressBar(async () => {
                        window.localStorage.removeItem(ONSEN_AUTH_TOKEN_KEY);
                        await apolloClient.resetStore();
                      })
                    }
                  />
                </>
              )}
            </NavbarMenu>
          </>
        )}
      </Stack>
    </navbarContext.Provider>
  );
}

function HomeLogo() {
  const { navbarWidth } = useContext(navbarContext);
  const mobileLayout = (navbarWidth || 1) <= MOBILE_LAYOUT_BREAKPOINT;
  const [data] = useData(gql`
    query HomeLogo {
      currentUser {
        id
        isPublicLeaguePersona
        publicLeagueTitle
        publicLeagueLogo
      }
    }
  `);

  return (
    <a
      style={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        fontSize: 16,
        gap: SPACING,
        textDecoration: "none",
        color: "inherit",
        paddingLeft: SPACING,
        paddingRight: SPACING,
      }}
      href="/"
    >
      {data?.currentUser?.isPublicLeaguePersona ? (
        <>
          <img
            src={data?.currentUser.publicLeagueLogo}
            style={{
              flex: "none",
              width: 50,
            }}
          />
          {mobileLayout && (
            <span
              style={{
                fontSize: "1.1em",
                flex: "auto",
                fontWeight: "bold",
              }}
            >
              {data?.currentUser.publicLeagueTitle}
            </span>
          )}
        </>
      ) : (
        <img
          src={glLogo}
          style={{
            width: 120,
          }}
          alt="Glory League Logo"
        />
      )}
    </a>
  );
}

// Note: NavbarItem is used for non-mobile layout
function NavbarItem({ label, href, icon = null, currentPathname = "" }) {
  const [hovering, hoveringSet] = useState(false);
  const highlightColor = COLOR_WHITE_LIGHTER;
  const normalizePath = (path = "") => {
    if (!path) return "";
    if (path === "/") return "/";
    return path.replace(/\/+$/, "");
  };
  const targetPathname = normalizePath(href?.split("?")?.[0] || "");
  const activePathname = normalizePath(currentPathname);

  const isActive = Boolean(targetPathname) && targetPathname === activePathname;

  const borderBottomColor = isActive || hovering ? highlightColor : "transparent";
  const menuColor = isActive || hovering ? COLOR_WHITE_LIGHTER : alpha(COLOR_WHITE_LIGHTER, 0.9);
  const menuFontWeight = isActive || hovering ? 500 : 400;
  return (
    <>
      <ButtonBase
        focusRipple
        onMouseEnter={() => hoveringSet(true)}
        onMouseLeave={() => hoveringSet(false)}
        style={{
          outline: "initial",
          display: "flex",
          alignItems: "center",
          gap: SPACING_DENSE,
          fontSize: 14,
          flex: "initial",
          height: 56,
          color: menuColor,
          padding: `${SPACING_HALF} ${SPACING}`,
          borderBottomWidth: 2,
          borderBottomStyle: "solid",
          borderBottomColor,
          ...(hovering && {
            backgroundColor: alpha(COLOR_WHITE_LIGHTER, 0.1),
          }),
        }}
        href={href}
      >
        {icon &&
          cloneElement(icon, {
            style: {
              fontSize: "1em",
              ...(hovering && {
                opacity: 0.8,
              }),
            },
          })}
        <span
          style={{
            fontWeight: menuFontWeight,
            fontFamily: FONT_FAMILY_GRAPHIK,
          }}
        >
          {label}
        </span>
      </ButtonBase>
    </>
  );
}

// Note: NavbarMenu is used for mobile layout
function NavbarMenu({ icon, label, children }) {
  const { navbarWidth } = useContext(navbarContext);
  const [open, openSet] = useState(false);
  const ref = useRef();
  const mobileLayout = navbarWidth <= MOBILE_LAYOUT_BREAKPOINT;
  const triggerIcon = open && mobileLayout ? <Close /> : icon;

  return (
    <navMenuContext.Provider value={{ open, openSet, navbarWidth }}>
      <ButtonBase
        ref={ref}
        focusRipple
        style={{
          display: "flex",
          padding: SPACING,
          alignItems: "center",
          flex: "none",
          fontSize: "14px",
          cursor: "pointer",
          fontFamily: FONT_FAMILY_GRAPHIK,
          gap: SPACING_DENSE,
          height: mobileLayout ? 46 : 56,
        }}
        onClick={() => openSet((prev) => !prev)}
      >
        {triggerIcon &&
          cloneElement(triggerIcon, {
            style: {
              fontSize: "1.6em",
            },
          })}
        {label}
      </ButtonBase>
      <Menu
        variant="menu"
        anchorEl={ref.current}
        open={open}
        onClose={() => openSet(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ disablePadding: true }}
        {...(mobileLayout
          ? {
              slotProps: {
                paper: {
                  sx: {
                    borderRadius: 0,
                    width: "100vw",
                    maxWidth: "100vw",
                    left: "0 !important",
                  },
                },
              },
            }
          : {
              slotProps: {
                paper: {
                  sx: {
                    borderRadius: 4,
                    minWidth: 260,
                    filter: `drop-shadow(0px 2px 8px ${alpha(COLOR_BLACK, 0.32)})`,
                  },
                },
              },
            })}
      >
        {children}
      </Menu>
    </navMenuContext.Provider>
  );
}

const NavbarMenuItem = forwardRef(function NavbarMenuItem({ label, icon, href, target, onClick }, ref) {
  const { openSet } = useContext(navMenuContext);
  const { navbarWidth } = useContext(navMenuContext);
  const mobileLayout = navbarWidth <= MOBILE_LAYOUT_BREAKPOINT;
  return (
    <MenuItem
      ref={ref}
      style={{
        fontSize: "13px",
        height: 48,
        width: "100%",
        borderBottom: mobileLayout ? `1px solid ${alpha(COLOR_WHITE_LIGHTER, 0.1)}` : "none",
        backgroundColor: mobileLayout ? COLOR_BLACK : COLOR_WHITE,
        color: mobileLayout ? COLOR_WHITE_LIGHTER : COLOR_BLACK,
        paddingLeft: SPACING,
      }}
      component="a"
      href={href || "#"}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      onClick={(event) => {
        onClick?.(event);
        openSet(false);
      }}
    >
      <ListItemIcon>
        {icon &&
          cloneElement(icon, {
            style: {
              fontSize: "1.3em",
              color: mobileLayout ? COLOR_WHITE_LIGHTER : COLOR_BLACK,
            },
          })}
      </ListItemIcon>
      <ListItemText primaryTypographyProps={{ fontSize: "1em", fontFamily: FONT_FAMILY_GRAPHIK }}>{label}</ListItemText>
    </MenuItem>
  );
});
